import {useContext} from 'react';

import {AssignmentContext} from 'client/AssignmentPracticeView/assignmentPracticeView.utils';

import {useMyGuessesForAssignmentQuery} from '../queries/studentGuesses.queries.generated';

type StudentGuess = NonNullable<
  ReturnType<typeof useMyGuessesForAssignmentQuery>['data']
>['myGuesses'][number];

/**
 * The student's own guess for a question in the assignment they're working in, read through
 * `myGuesses` — the only guess route students may call.
 *
 * Resolves undefined outside an assignment (independent practice, adaptive) and for teachers,
 * who have no guesses of their own to read. Both are gated by `AssignmentContext` being absent
 * or the query being skipped, never by a forbidden read.
 *
 * Deliberately never selects `status`: a field policy restricts it to teachers and admins so
 * students cannot infer where in grading their submission sits.
 *
 * `teacherEvaluation` is field-policy gated on release, and one assignment can mix released and
 * unreleased guesses, so denial for some rows is the normal case rather than a failure —
 * `errorPolicy: 'all'` keeps the rest of the row either way. Read it only when `gradeAvailable`,
 * whose definition is that same policy.
 *
 * Returns `loading` alongside the guess because an undefined guess is ambiguous on its own: it
 * means both "still in flight" and "this student has no guess here". Callers that render
 * different states for those two need to tell them apart, or they paint the wrong one first.
 */
export default function useStudentGuess(questionId: string | undefined): {
  guess: StudentGuess | undefined;
  loading: boolean;
} {
  const {assignment} = useContext(AssignmentContext);
  const assignmentId: string | undefined = assignment?.getId?.();

  const {data, loading} = useMyGuessesForAssignmentQuery({
    variables: {assignmentId: assignmentId ?? ''},
    skip: !assignmentId || !questionId,
    errorPolicy: 'all'
  });

  return {
    guess: data?.myGuesses.find((guess) => guess.questionId === questionId),
    loading
  };
}
